import { useState } from 'react';

export interface TestingUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  userType: string;
  profileImageUrl?: string;
}

// Mock user data for testing different roles
const testingUsers: Record<string, TestingUser> = {
  patient: {
    id: '41709313',
    email: 'haltaie@pm.me',
    firstName: 'Hussein',
    lastName: 'Al-Taie',
    userType: 'patient',
    profileImageUrl: 'https://replit.com/public/images/mark.png',
  },
  doctor: {
    id: 'doctor_001',
    email: 'dr.ahmed@tabib.health',
    firstName: 'Ahmed',
    lastName: 'Al-Rashid',
    userType: 'doctor',
    profileImageUrl: 'https://replit.com/public/images/mark.png',
  },
  hospital: {
    id: 'hospital_001',
    email: 'admin@baghdad-hospital.iq',
    firstName: 'Fatima',
    lastName: 'Al-Zahra',
    userType: 'hospital',
    profileImageUrl: 'https://replit.com/public/images/mark.png',
  },
  pharmacy: {
    id: 'pharmacy_001',
    email: 'manager@green-pharmacy.iq',
    firstName: 'Omar',
    lastName: 'Al-Khalil',
    userType: 'pharmacy',
    profileImageUrl: 'https://replit.com/public/images/mark.png',
  },
  lab: {
    id: 'lab_001',
    email: 'lab@medical-center.iq',
    firstName: 'Zainab',
    lastName: 'Al-Hashimi',
    userType: 'lab',
    profileImageUrl: 'https://replit.com/public/images/mark.png',
  },
  health_services: {
    id: 'health_001',
    email: 'services@health-org.iq',
    firstName: 'Kareem',
    lastName: 'Al-Mansouri',
    userType: 'health_services',
    profileImageUrl: 'https://replit.com/public/images/mark.png',
  },
};

// Testing mode is a build-time, developer-only escape hatch that swaps the
// real session for a mock user. It is OFF in every environment — including
// local `npm run dev` and production — and the ONLY way to turn it on is to
// set `VITE_TESTING_MODE=true` in the environment before Vite starts. There
// is deliberately no `localStorage` flag, no `import.meta.env.DEV` heuristic,
// and no runtime code path that can enable it: a stale browser flag must
// never be able to hijack the real auth flow and make the app impossible to
// test against the live API (regression: 023-ai-assistant-fix QA).
const testingModeEnabled = import.meta.env.VITE_TESTING_MODE === 'true';

export function useTestingMode() {
  const [currentTestRole, setCurrentTestRole] = useState<string>(() => {
    if (!testingModeEnabled) return 'patient';
    const savedRole = localStorage.getItem('tabib_test_role');
    return savedRole && testingUsers[savedRole] ? savedRole : 'patient';
  });

  const switchTestRole = (role: string) => {
    if (!testingModeEnabled || !testingUsers[role]) return;
    setCurrentTestRole(role);
    localStorage.setItem('tabib_test_role', role);
    // Force a page reload to update the authentication state
    window.location.reload();
  };

  const getTestUser = (role?: string): TestingUser | null => {
    if (!testingModeEnabled) return null;
    const roleToUse = role || currentTestRole;
    return testingUsers[roleToUse] || null;
  };

  return {
    isTestingMode: testingModeEnabled,
    currentTestRole,
    switchTestRole,
    getTestUser,
  };
}
